import { LineChartBlock, VizSummaryKind } from '../../viz.types';
import { getNumberFormatter } from '../../viz.utils';

export interface LineChartSummary {
  value: number;
  caption: string | null;
  deltaRatio: number | null;
}

const DELTA_OPTIONS: Intl.NumberFormatOptions = {
  style: 'percent',
  maximumFractionDigits: 1,
};

export interface GetLineChartSummaryParams {
  series: LineChartBlock['series'];
  kind?: VizSummaryKind | null;
}

const getDeltaRatio = (series: LineChartBlock['series']) => {
  const firstPoint = series.at(0);
  const lastPoint = series.at(-1);

  if (!firstPoint || !lastPoint || series.length < 2 || firstPoint.y === 0) {
    return null;
  }

  if (!Number.isFinite(firstPoint.y) || !Number.isFinite(lastPoint.y)) {
    return null;
  }

  return (lastPoint.y - firstPoint.y) / Math.abs(firstPoint.y);
};

export const getLineChartSummary = ({
  series,
  kind,
}: GetLineChartSummaryParams): LineChartSummary | null => {
  if (!kind) {
    return null;
  }

  if (kind === 'total') {
    const total = series.reduce((sum, point) => sum + (Number.isFinite(point.y) ? point.y : 0), 0);

    return { value: total, caption: null, deltaRatio: null };
  }

  const lastPoint = series.at(-1);

  if (!lastPoint || !Number.isFinite(lastPoint.y)) {
    return null;
  }

  return { value: lastPoint.y, caption: lastPoint.x, deltaRatio: getDeltaRatio(series) };
};

export const getLineChartEndValue = (series: LineChartBlock['series']) => {
  const lastPoint = series.at(-1);

  return lastPoint && Number.isFinite(lastPoint.y) ? lastPoint.y : null;
};

export const formatDeltaRatio = (deltaRatio: number, locale?: string) => {
  const formatted = getNumberFormatter(locale, DELTA_OPTIONS).format(Math.abs(deltaRatio));

  return deltaRatio < 0 ? `-${formatted}` : `+${formatted}`;
};
